/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useMemo } from 'react';
import moment from 'moment';
import {
  IoCalendarOutline,
  IoCallOutline,
  IoLocationOutline,
  IoMailOutline,
  IoMapOutline,
  IoTimeOutline,
} from 'react-icons/io5';
import { HiOutlineBuildingOffice2, HiOutlineTag } from 'react-icons/hi2';
import { ServiceGet } from '../../../utils/type';
import ServiceCard from '../../components/common/ui/ServiceCard';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import NotFound from '../../../utils/erros/NotFound';
import ProviderInfoRow from './components/ProviderInfoRow';
import { PROVIDER_SERVICES_ANCHOR_ID } from './components/constants';

interface ProviderProps {
  service: ServiceGet[];
  providerData: any;
  isProviderLoading: boolean;
  isServiceLoading: boolean;
}

/** A row is only included when it has a real, non-empty value. */
interface InfoRowSpec {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}

const SectionCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) => (
  <section className="rounded-2xl border border-border bg-white p-5">
    <h2 className="text-base font-bold text-brandnavy">{title}</h2>
    <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
    <div className="mt-4 border-t border-border pt-1">{children}</div>
  </section>
);

const ProviderOrverview = ({
  providerData,
  service,
  isProviderLoading,
  isServiceLoading,
}: ProviderProps) => {
  // Helper to convert "17:00" to "5:00 PM"
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const [hour, minute] = timeStr.split(':');
    const hourInt = parseInt(hour, 10);
    const ampm = hourInt >= 12 ? 'PM' : 'AM';
    const formattedHour = hourInt % 12 || 12; // Convert 0 to 12 for 12 AM
    return `${formattedHour}:${minute} ${ampm}`;
  };

  /** Returns '' (not a placeholder) when there is no usable schedule. */
  const formatOperatingHours = (hours: any, isFullDay?: boolean) => {
    if (isFullDay) {
      return 'Open 24 hours, 7 days a week';
    }
    if (!hours) return '';

    const schedule: { [key: string]: string[] } = {};
    const daysOrder = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];

    daysOrder.forEach((day: any) => {
      const dayInfo = hours[day];
      if (dayInfo && dayInfo.isActive) {
        const timeRange = `${formatTime(dayInfo.open)} - ${formatTime(
          dayInfo.close
        )}`;
        if (!schedule[timeRange]) {
          schedule[timeRange] = [];
        }
        schedule[timeRange].push(day.charAt(0).toUpperCase() + day.slice(1, 3));
      }
    });

    return Object.entries(schedule)
      .map(([timeRange, days]) => `${days.join(', ')}: ${timeRange}`)
      .join(' | ');
  };

  // providerData is now a Business object from /v1/business/by-slug/:businessSlug
  const aboutText: string = providerData?.bio || '';

  const memberSince = useMemo(() => {
    if (!providerData?.createdAt) return '';
    const parsed = moment(providerData.createdAt);
    return parsed.isValid() ? parsed.format('MMM DD, YYYY') : '';
  }, [providerData?.createdAt]);

  const operatingHours = formatOperatingHours(
    providerData?.operatingHours,
    providerData?.isFullDay
  );

  // --- Business fields read directly from the Business object ---
  const businessCategory: string | undefined = providerData?.category;
  const businessEmail: string | undefined = providerData?.email;
  const businessContactNo: string | undefined = providerData?.contactNo;

  const businessLocation = useMemo(() => {
    const loc = providerData?.location;
    if (!loc) return '';
    return [
      loc.addressLine1,
      loc.addressLine2,
      loc.city,
      loc.state,
      loc.postalCode,
      loc.country,
    ]
      .filter(Boolean)
      .join(', ');
  }, [providerData?.location]);

  /** Provider Information rows — only those with real values. */
  const providerInfoRows = useMemo<InfoRowSpec[]>(() => {
    const rows: InfoRowSpec[] = [];
    if (operatingHours) {
      rows.push({
        icon: <IoTimeOutline size={18} />,
        label: 'Operating Hours',
        value: operatingHours,
      });
    }
    if (businessCategory) {
      rows.push({
        icon: <HiOutlineTag size={18} />,
        label: 'Business Category',
        value: businessCategory,
      });
    }
    if (providerData?.serviceArea) {
      rows.push({
        icon: <IoMapOutline size={18} />,
        label: 'Service Area',
        value: providerData.serviceArea,
      });
    }
    if (memberSince) {
      rows.push({
        icon: <IoCalendarOutline size={18} />,
        label: 'Member Since',
        value: memberSince,
      });
    }
    return rows;
  }, [
    operatingHours,
    businessCategory,
    providerData?.serviceArea,
    memberSince,
  ]);

  /** Contact Information rows — only those with real values. */
  const contactInfoRows = useMemo<InfoRowSpec[]>(() => {
    const rows: InfoRowSpec[] = [];
    if (businessEmail) {
      rows.push({
        icon: <IoMailOutline size={18} />,
        label: 'Email',
        value: (
          <a
            href={`mailto:${businessEmail}`}
            className="cursor-pointer text-brandOcean transition-colors duration-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandcyan motion-reduce:transition-none"
          >
            {businessEmail}
          </a>
        ),
      });
    }
    if (businessLocation) {
      rows.push({
        icon: <IoLocationOutline size={18} />,
        label: 'Location',
        value: businessLocation,
      });
    }
    if (providerData?.serviceBranches) {
      rows.push({
        icon: <HiOutlineBuildingOffice2 size={18} />,
        label: 'Branches',
        value: providerData.serviceBranches,
      });
    }
    if (businessContactNo) {
      rows.push({
        icon: <IoCallOutline size={18} />,
        label: 'Contact No',
        value: (
          <a
            href={`tel:${businessContactNo}`}
            className="cursor-pointer text-brandOcean transition-colors duration-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandcyan motion-reduce:transition-none"
          >
            {businessContactNo}
          </a>
        ),
      });
    }
    return rows;
  }, [
    businessEmail,
    businessLocation,
    providerData?.serviceBranches,
    businessContactNo,
  ]);

  const showAbout = isProviderLoading || Boolean(aboutText);
  const showProviderInfo = providerInfoRows.length > 0;
  const showContactInfo = contactInfoRows.length > 0;
  const showInfoGrid = showAbout || showProviderInfo || showContactInfo;

  return (
    <div>
      {showInfoGrid && (
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {showAbout && (
            <SectionCard
              title="About us"
              subtitle="Our values and commitment to excellence"
            >
              {isProviderLoading ? (
                <div className="py-3 text-xs text-gray-400">Loading...</div>
              ) : (
                <p className="whitespace-pre-line break-words py-3 text-sm leading-6 text-gray-600">
                  {aboutText}
                </p>
              )}
            </SectionCard>
          )}

          {showProviderInfo && (
            <SectionCard
              title="Provider Information"
              subtitle="Business details"
            >
              <div className="divide-y divide-border">
                {providerInfoRows.map((row) => (
                  <ProviderInfoRow
                    key={row.label}
                    icon={row.icon}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </div>
            </SectionCard>
          )}

          {showContactInfo && (
            <SectionCard title="Contact Information" subtitle="Get in touch">
              <div className="divide-y divide-border">
                {contactInfoRows.map((row) => (
                  <ProviderInfoRow
                    key={row.label}
                    icon={row.icon}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      <div id={PROVIDER_SERVICES_ANCHOR_ID} className="mt-8 scroll-mt-6">
        <div className="flex flex-col items-start">
          <h2 className="text-base font-bold text-brandnavy">
            Services We Provide
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Explore how our comprehensive services help you achieve your goals
            efficiently and effectively
          </p>
        </div>

        {isServiceLoading ? (
          <div className="mt-6 flex items-center justify-center">
            <SmallLoadingSpinner label="Loading..." />
          </div>
        ) : service?.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {service.map((item: any, index: number) => (
              <div key={item?.serviceId || index}>
                <ServiceCard data={item} user={providerData} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5">
            <NotFound />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderOrverview;
